import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table, Select, Input, Button } from 'antd';
import { FilterValue, SorterResult, TablePaginationConfig } from 'antd/lib/table/interface';
import { DownloadOutlined } from '@ant-design/icons';
import {
    getFreeRoundsCampaignPlayersData,
    getFreeRoundsCampaignPlayersTotalData,
    isExportingPlayersCsvData,
    shouldUpdateData,
} from 'redux/selectors/backoffice/propmotions-selectors';
import {
    exportPlayersCsv,
    getFreeRoundsCampaignPlayers,
} from 'redux/actions/backoffice/promotions-actions';
import PlayersTableColumns from './PlayersTableColumns';

export type SortOrder = 'DESCENDING' | 'ASCENDING';
interface SortConfig {
    sortKey: string;
    sortOrder: SortOrder;
}

const DEFAULT_SORT_CONFIG: SortConfig = { sortKey: 'id', sortOrder: 'DESCENDING' };
const DEFAULT_AMOUNT_OF_ITEMS_PER_PAGE = 20;
const DEFAULT_PAGINATION = { pageNumber: 1, pageSize: DEFAULT_AMOUNT_OF_ITEMS_PER_PAGE };

interface PlayersTableProps {
    campaignId: string | undefined;
    currencyCode: string;
}

const PlayersTable: React.FC<PlayersTableProps> = ({ campaignId = 0, currencyCode }) => {
    const dispatch = useDispatch();
    const players = useSelector(getFreeRoundsCampaignPlayersData) || [];
    const playersTotal = useSelector(getFreeRoundsCampaignPlayersTotalData) ?? 0;
    const isExporting = useSelector(isExportingPlayersCsvData);
    const shouldUpdate = useSelector(shouldUpdateData);
    const [searchTerm, setSearchTerm] = useState('');
    const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
    const [sortConfig, setSortConfig] = useState<SortConfig>(DEFAULT_SORT_CONFIG);
    const [playersStatus, setPlayersStatus] = useState<string>('all');
    const playerColumns = PlayersTableColumns(currencyCode);
    const [tableHeight, setTableHeight] = useState(0);
    const totalWidth: number = playerColumns.reduce(
        (total: number, col: any) => total + (col.width || 0),
        0,
    );

    const calculateTableHeight = useCallback(() => {
        const height = window.innerHeight - 570;
        setTableHeight(height);
    }, []);

    useEffect(() => {
        calculateTableHeight();
        window.addEventListener('resize', calculateTableHeight);

        return () => window.removeEventListener('resize', calculateTableHeight);
    }, [calculateTableHeight]);

    const fetchPlayers = useCallback(() => {
        dispatch(
            getFreeRoundsCampaignPlayers({
                campaignId,
                paginationData,
                sortKey: sortConfig.sortKey,
                sortOrder: sortConfig.sortOrder,
                playerIdSearchValue: searchTerm || null,
                playersStatus,
                currency: currencyCode,
            })
        );
    }, [dispatch, campaignId, paginationData, sortConfig, searchTerm, playersStatus, currencyCode]);

    useEffect(() => {
        fetchPlayers();
    }, [fetchPlayers, paginationData, sortConfig, searchTerm, playersStatus]);

    useEffect(() => {
        if (shouldUpdate) fetchPlayers();
    }, [fetchPlayers, shouldUpdate]);

    const handleStatusChange = (status: string) => {
        setPlayersStatus(status);
        setPaginationData({ ...paginationData, pageNumber: 1 });
    };

    const handlePagination = (page: number, pageSize?: number) => {
        setPaginationData({ pageNumber: page, pageSize: pageSize || DEFAULT_PAGINATION.pageSize });
    };

    const handleSort = (sorter: SorterResult<any> | SorterResult<any>[]) => {
        if (Array.isArray(sorter) && sorter.length && sorter[0].order) {
            setSortConfig({
                sortKey: sorter[0].field?.toString() || 'id',
                sortOrder: sorter[0].order === 'ascend' ? 'ASCENDING' : 'DESCENDING',
            });
        } else if (!Array.isArray(sorter) && sorter.field && sorter.order) {
            setSortConfig({
                sortKey: sorter.field.toString(),
                sortOrder: sorter.order === 'ascend' ? 'ASCENDING' : 'DESCENDING',
            });
        }
    };

    const onTableChange = (
        pagination: TablePaginationConfig,
        _filters: Record<string, FilterValue | null>,
        sorter: SorterResult<any> | SorterResult<any>[]
    ) => {
        handlePagination(pagination.current ?? 1, pagination.pageSize);
        handleSort(sorter);
    };

    const handleExport = () => {
        dispatch(
            exportPlayersCsv({
                campaignId,
                players,
                paginationData: {
                    pageNumber: 1,
                    pageSize: playersTotal || paginationData.pageSize,
                },
                sortKey: sortConfig.sortKey,
                sortOrder: sortConfig.sortOrder,
                playerIdSearchValue: searchTerm || null,
                playersStatus,
                currency: currencyCode,
            }),
        );
    };

    return (
        <div className="pt-2 flex flex-col h-full">
            <div className="flex items-center mb-4 gap-2">
                <Select
                    defaultValue="all"
                    style={{ width: 180 }}
                    value={playersStatus}
                    onChange={handleStatusChange}
                    options={[
                        { value: 'all', label: 'All' },
                        { value: 'QUEUED', label: 'Queued' },
                        { value: 'IN_USE', label: 'In Use' },
                        { value: 'OPT_OUT', label: 'Opted Out' },
                        { value: 'COMPLETED', label: 'Completed' },
                        { value: 'EXPIRED', label: 'Expired' },
                    ]}
                />
                <Input.Search
                    placeholder="Search"
                    value={searchTerm}
                    onSearch={setSearchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                />
                <Button
                    icon={<DownloadOutlined />}
                    onClick={handleExport}
                    loading={isExporting}
                >
                    Export to CSV
                </Button>
            </div>
            <Table
                    virtual
                    className="w-full max-h-[calc(100vh-420px)]"
                    rowKey="playerId"
                    size="small"
                    columns={playerColumns}
                    dataSource={players as any[]}
                    pagination={{
                        current: paginationData.pageNumber,
                        pageSize: paginationData.pageSize,
                        total: playersTotal,
                        showSizeChanger: true,
                        onChange: handlePagination,
                    }}
                    scroll={{ y: tableHeight, x: totalWidth }}
                    onChange={onTableChange}
                />
        </div>
    );
};

export default PlayersTable; 
